import {
  type LogGroup,
  type LogOverviewRow,
  useSetOrderSilencing,
} from '@/api/hooks/useIntegrationLogs'
import { VerticalDots } from '@/components/Display/VerticalDots'
import type { SagaType } from '@/constants/sagaTypes'
import { Dropdown, Tooltip } from 'antd'
import { useState } from 'react'
import {
  SilencingCheckboxList,
  SilencingPanel,
  useSilenceableSagas,
} from './SilencingCheckboxList'

type OrderCheckpoints = Extract<
  LogOverviewRow['checkpoints'],
  { type: 'OrderIntegrationCheckpoints' }
>

export function CheckpointSilencingMenu({
  row,
  onSaved,
  onClosed,
}: {
  row: LogOverviewRow
  /** Writes the saved checkpoints onto the grid row. */
  onSaved?: (groups: LogGroup[]) => void
  /** Refetches the grid, so Hide Silenced can drop the row. */
  onClosed?: () => void
}) {
  const sagas = useSilenceableSagas()
  const setSilencing = useSetOrderSilencing()
  // The refetch waits for the menu to close: each checkbox commits on its own,
  // so refetching per click would pull the row out from under the open menu.
  const [savedSomething, setSavedSomething] = useState(false)
  // Held while the request is in flight so the checkbox flips immediately
  // rather than waiting for the overview grid to refetch.
  const [pending, setPending] = useState<SagaType[] | null>(null)

  if (row.checkpoints.type !== 'OrderIntegrationCheckpoints') return null
  const checkpoints: OrderCheckpoints = row.checkpoints

  const silenced = pending ?? checkpoints.silenced_sagas

  // Committed per click here -- unlike the bulk dialog, there's no Save step.
  const save = (next: SagaType[]) => {
    setPending(next)
    setSilencing.mutate(
      { body: { log_group_ids: [row.id], sagas_to_silence: next } },
      {
        onSuccess: (groups) => {
          setSavedSomething(true)
          // Has to land before `pending` is dropped, or the checkbox falls back
          // to the row's stale checkpoints and appears to undo itself.
          onSaved?.(groups)
        },
        onSettled: () => setPending(null),
      },
    )
  }

  const onOpenChange = (open: boolean) => {
    if (open || !savedSomething) return
    setSavedSomething(false)
    onClosed?.()
  }

  const count = silenced.length

  return (
    <Dropdown
      onOpenChange={onOpenChange}
      dropdownRender={() => (
        <SilencingPanel title='Silence notifications for'>
          <SilencingCheckboxList
            sagas={sagas}
            value={silenced}
            onChange={save}
            disabled={setSilencing.isPending}
          />
        </SilencingPanel>
      )}
      trigger={['click']}
      placement='bottomRight'
      getPopupContainer={() => document.body}
    >
      <Tooltip
        title={
          count > 0
            ? `${count} checkpoint${count === 1 ? '' : 's'} silenced`
            : 'Silence checkpoints'
        }
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            cursor: 'pointer',
            color: count > 0 ? 'var(--theme-warning)' : 'var(--gray-6)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <VerticalDots />
        </div>
      </Tooltip>
    </Dropdown>
  )
}
